/**
 * NKROService
 * 
 * Manages the guided N-Key Rollover / Ghosting diagnostic flow.
 * It uses a zone-based approach, prompting the user to press common 
 * problem-area key combinations to verify the keyboard's maximum rollover.
 */
export class NKROService extends EventTarget {
  constructor() {
    super();
    this.isActive = false;
    this.currentStepIndex = 0;
    this.results = [];

    this.ZONES = [
      { id: 'wasd', name: 'W-A-S-D', keys: ['KeyW', 'KeyA', 'KeyS', 'KeyD'], description: 'Press W, A, S, and D together' },
      { id: 'arrows', name: 'ARROW_KEYS', keys: ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'], description: 'Press all arrow keys together' },
      { id: 'home_row', name: 'HOME_ROW', keys: ['KeyA', 'KeyS', 'KeyD', 'KeyF', 'KeyJ', 'KeyK', 'KeyL', 'Semicolon'], description: 'Press all home row keys (ASDF JKL;)' },
      { id: 'modifiers', name: 'MODIFIERS', keys: ['ControlLeft', 'ShiftLeft', 'AltLeft', 'Space'], description: 'Press Ctrl, Shift, Alt, and Space together' }
    ];
  }

  /**
   * Resets and starts the guided NKRO wizard from the first zone.
   */
  startWizard() {
    this.isActive = true;
    this.currentStepIndex = 0;
    this.results = [];
    this.dispatchStep();
  }

  /**
   * Transitions to the next zone in the wizard.
   * 
   * @param {number} successCount - How many keys in the current zone were successfully registered.
   */
  nextStep(successCount) {
    const currentZone = this.ZONES[this.currentStepIndex];
    this.results.push({
      zone: currentZone.id,
      expected: currentZone.keys.length,
      actual: successCount,
      pass: successCount >= currentZone.keys.length
    });

    this.currentStepIndex++;
    if (this.currentStepIndex >= this.ZONES.length) {
      this.complete();
    } else {
      this.dispatchStep();
    }
  }

  dispatchStep() {
    this.dispatchEvent(new CustomEvent('wizardstep', {
      detail: {
        step: this.currentStepIndex + 1,
        total: this.ZONES.length,
        zone: this.ZONES[this.currentStepIndex]
      }
    }));
  }

  complete() {
    this.isActive = false;
    this.dispatchEvent(new CustomEvent('wizardcomplete', {
      detail: { results: this.results }
    }));
  }

  cancel() {
    this.isActive = false;
    this.dispatchEvent(new CustomEvent('wizardcancel'));
  }
}

export const nkroService = new NKROService();
